import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { Select2 } from '@/components/forms/Select2';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { Switch } from '@/components/forms/Switch';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useToast } from '@/components/common/toast';
import { http } from '@/api/client/httpClient';
import { filterDataApi } from '@/api/services/filterData';
import { todayDateString, yesterdayDateString } from '@/api/services/riskDashboard';
import { portfolioEquityApi } from '@/api/services/portfolioEquity';
import type { PortfolioEquityFilterInput } from '@/api/types/portfolioEquity';
import { SummaryTab } from './SummaryTab';
import { DetailedTab } from './DetailedTab';
import './PortfolioEquity.css';

const TABS: TabItem[] = [
  { key: 'summary', title: 'Summary Report' },
  { key: 'detailed', title: 'Detailed Report' },
];

export function PortfolioEquityPage() {
  const toast = useToast();
  const [tab, setTab] = useState<'summary' | 'detailed'>('summary');
  const [filters, setFilters] = useState<PortfolioEquityFilterInput>({ referenceDate: yesterdayDateString(), isLive: false, portfolioProfileId: null });
  const [applied, setApplied] = useState<PortfolioEquityFilterInput>(filters);
  const today = todayDateString();
  // Force a fresh request only for live / today data; keep the cache for historical.
  // fetchPulse gives the Fetch button a brief spinner even on a cache hit.
  const [fetchNonce, setFetchNonce] = useState(0);
  const [fetchPulse, setFetchPulse] = useState(false);

  const portfolios = useQuery({ queryKey: ['rd-filter', 'portfolios'], queryFn: filterDataApi.getPortfolios, staleTime: 5 * 60_000 });
  const summary = useQuery({ queryKey: ['pe-summary', applied, fetchNonce], queryFn: () => portfolioEquityApi.getSummary(applied) });
  const detailed = useQuery({
    queryKey: ['pe-detailed', applied, fetchNonce],
    queryFn: () => portfolioEquityApi.getDetailed({ ...applied, skipCount: 0, maxResultCount: 5000 }),
    enabled: tab === 'detailed',
  });

  // Legacy honours a ?live=1 query param on load: force today + live (portfolio-equity-page.js applyLiveFromQuery).
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('live') !== '1') return;
    setFilters((f) => ({ ...f, referenceDate: today, isLive: true }));
    setApplied((f) => ({ ...f, referenceDate: today, isLive: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetch = () => {
    setApplied({ ...filters });
    if (filters.isLive || filters.referenceDate === today) setFetchNonce((n) => n + 1);
    setFetchPulse(true);
    window.setTimeout(() => setFetchPulse(false), 450);
  };

  async function extract() {
    try {
      const res = await http.get(portfolioEquityApi.extractExcelUrl, { params: applied, responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Portfolio Equity - ${applied.referenceDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export Portfolio Equity.');
    }
  }

  // Surface warning/error from summary.
  const s = summary.data;
  const msg = s?.errorMessage || s?.warning;
  useEffect(() => {
    if (s?.errorMessage) toast.error(s.errorMessage);
    else if (s?.warning) toast.info(s.warning);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg]);

  const str = (v: string | number | null | undefined) => (v == null ? '' : String(v));
  const fields = [
    {
      node: (
        <DateRangePicker
          key="date"
          label="Reference Date"
          mode="single"
          maxDate={today}
          value={filters.referenceDate}
          onChange={(v) => setFilters((f) => ({ ...f, referenceDate: v, isLive: v === today ? f.isLive : false }))}
        />
      ),
      pinned: true,
      width: 160,
    },
    <Select2
      key="pf"
      label="Portfolio"
      allowClear
      placeholder="All"
      value={str(filters.portfolioProfileId)}
      options={portfolios.data ?? []}
      onChange={(v) => setFilters((f) => ({ ...f, portfolioProfileId: v ? Number(v) : null }))}
    />,
  ];

  const modes = (
    <Switch
      label="Live Data"
      checked={filters.isLive}
      onChange={(checked) => setFilters((f) => ({ ...f, isLive: checked, referenceDate: checked ? today : f.referenceDate }))}
    />
  );

  const actions = (
    <>
      <button type="button" className="btn btn-primary btn-sm" onClick={fetch} disabled={summary.isFetching || fetchPulse}>
        {summary.isFetching || fetchPulse ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}Fetch
      </button>
      <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-download" onClick={extract}>
        Extract
      </AsyncButton>
    </>
  );

  return (
    <div className="portfolio-equity-page">
      <PageHeader title="Portfolio Equity" breadcrumb={['Home', 'Portfolio Equity']} />

      <div className="card custom-card mb-0">
        <div className="card-header border-bottom">
          <FilterBar card={false} fields={fields} modes={modes} actions={actions} inlineActions />
        </div>
        <div className="card-header p-0">
          <Tabs tabs={TABS} active={tab} onChange={(k) => setTab(k as 'summary' | 'detailed')} variant="underline" />
        </div>
        <div className="card-body">
          {tab === 'summary' && <SummaryTab summary={summary.data} isLoading={summary.isLoading} />}
          {tab === 'detailed' && <DetailedTab rows={detailed.data?.items ?? []} isLoading={detailed.isFetching} />}
        </div>
      </div>
    </div>
  );
}
